import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { NumerosALetras } from "numero-a-letras";
import moment from "moment";
import "moment/locale/es";

// Set moment to Spanish locale
moment.locale("es");

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  // Accent bar
  accentBar: {
    height: 3,
    backgroundColor: "#5d7b9d",
    marginHorizontal: -30,
    marginBottom: 10,
  },
  // Header styles
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderColor: "#dddddd",
    marginBottom: 8,
  },
  logo: {
    width: 150,
  },
  titleContainer: {
    alignItems: "flex-end",
  },
  title: {
    fontSize: 20,
    color: "#888888",
    fontFamily: "Helvetica-Bold",
  },
  // Address section styles
  addressSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingBottom: 6,
    paddingTop: 0,
    fontSize: 9,
    color: "#666666",
  },
  addressLeft: {
    width: "60%",
    textAlign: "justify",
    lineHeight: 1.4,
  },
  addressRight: {
    width: "40%",
    textAlign: "right",
    fontFamily: "Helvetica-Bold",
  },
  // Info grid styles
  infoGrid: {
    backgroundColor: "#fafafa",
    borderRadius: 3,
    marginBottom: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: "#dddddd",
  },
  infoRow: {
    flexDirection: "row",
  },
  infoCell: {
    width: "50%",
    paddingVertical: 6,
    paddingHorizontal: 9,
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderColor: "#dddddd",
  },
  infoCellRight: {
    borderRightWidth: 0,
  },
  infoCellTotalLeft: {
    backgroundColor: "#eef3f8",
  },
  infoCellTotalRight: {
    backgroundColor: "#eef3f8",
  },
  infoLabel: {
    fontSize: 8,
    color: "#666666",
    marginBottom: 3,
  },
  infoValue: {
    fontSize: 10,
    color: "#000000",
    lineHeight: 1.4,
  },
  // Detalle section styles
  detalleHeader: {
    paddingLeft: 8,
    paddingVertical: 4,
    marginBottom: 6,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
    borderColor: "#5d7b9d",
  },
  detalleHeaderText: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
  },
  // Table styles
  table: {
    width: "100%",
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#5d7b9d",
    color: "#ffffff",
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderColor: "#dddddd",
    minHeight: 25,
  },
  tableRowAlt: {
    backgroundColor: "#f7f9fb",
  },
  tableCellCC: {
    width: "10%",
    padding: 8,
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderColor: "#dddddd",
    justifyContent: "center",
  },
  tableCellConcepto: {
    width: "35%",
    padding: 8,
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderColor: "#dddddd",
    justifyContent: "center",
  },
  tableCellDescripcion: {
    width: "40%",
    padding: 8,
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderColor: "#dddddd",
    justifyContent: "center",
  },
  tableCellMonto: {
    width: "15%",
    padding: 8,
    justifyContent: "center",
    textAlign: "right",
  },
  tableHeaderText: {
    color: "#ffffff",
  },
  tableCellText: {
    fontSize: 9,
    lineHeight: 1.4,
  },
  // Footer styles
  separator: {
    width: "80%",
    height: 1.5,
    backgroundColor: "#333333",
    marginHorizontal: "10%",
    marginVertical: 10,
  },
  finLineaText: {
    textAlign: "center",
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    marginBottom: 8,
  },
  montoLetrasContainer: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 10,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
    borderColor: "#5d7b9d",
  },
  montoLetrasText: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    lineHeight: 1.4,
  },
  footerContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 8,
  },
  footerText: {
    fontSize: 8,
    color: "#888888",
    fontStyle: "italic",
  },
  footerBold: {
    fontFamily: "Helvetica-Bold",
    fontStyle: "normal",
  },
});

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-HN", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parseFloat(value || 0));
};

// Helper function to convert number to words
const convertirNumeroALetras = (numero) => {
  const opciones = {
    moneda: "LEMPIRAS",
    centimos: "CENTAVOS",
  };
  let texto = NumerosALetras(numero, opciones);
  texto = texto.replace("Pesos", "LEMPIRAS").replace("M.N.", "").trim();
  texto = texto.replace(" de ", "  ");

  // Detect if there are cents
  const partes = numero.toFixed(2).split(".");
  const centavos = parseInt(partes[1], 10);

  if (centavos > 0) {
    texto = texto.replace(
      centavos + "/100",
      "CON " + centavos + "/100 CENTAVOS"
    );
  }

  return texto.toUpperCase();
};

// Helper function to sort detalles by concept code
const sortDetallesByConcepto = (detalles) => {
  const orderMap = {
    "CC-06": 1,
    "CC-20": 2,
    "FF-01": 3,
    "FF-02": 4,
  };

  return [...detalles].sort((a, b) => {
    const orderA = orderMap[a.codigoConceptoCobro] || 999;
    const orderB = orderMap[b.codigoConceptoCobro] || 999;
    return orderA - orderB;
  });
};

export const AvisoCobroPDF = ({ aviso, isPrueba = false }) => {
  console.log('[AvisoCobroPDF] Rendering PDF with aviso:', aviso?.numeroAviso);
  console.log('[AvisoCobroPDF] Full aviso data:', aviso);

  if (!aviso) {
    console.error('[AvisoCobroPDF] No aviso data provided');
    return null;
  }

  if (!aviso.numeroAviso) {
    console.error('[AvisoCobroPDF] Aviso is missing numeroAviso:', aviso);
  }

  if (!aviso.detalles || !Array.isArray(aviso.detalles)) {
    console.error('[AvisoCobroPDF] Aviso detalles is missing or not an array:', aviso.detalles);
  }

  const sortedDetalles = sortDetallesByConcepto(aviso.detalles || []);
  const totalEnLetras = convertirNumeroALetras(aviso.total || 0);
  const fechaEmision = moment(aviso.fechaEmision).utc().format("LL");
  const fechaVencimiento = moment(aviso.fechaVencimiento).utc().format("LL");

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Accent bar */}
        <View style={styles.accentBar} />

        {/* Header Section */}
        <View style={styles.header}>
          <Image
            src={`${window.location.origin}/img/logoCnt.jpg`}
            style={styles.logo}
          />
          <View style={styles.titleContainer}>
            <Text style={styles.title}>Aviso de Cobro</Text>
          </View>
        </View>

        {/* Address Section */}
        <View style={styles.addressSection}>
          <Text style={styles.addressLeft}>
            Edificio CONATEL, 6. Ave., S.O. Colonia Modelo, Comayagüela, M.D.C.
            Honduras, Centro América, Apartado Postal 15012,{" "}
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              Teléfono: (504) 2232-9600
            </Text>
          </Text>
          <Text style={styles.addressRight}>
            R.T.N CONATEL 08019995385949
          </Text>
        </View>

        {/* Info Grid Section */}
        <View style={styles.infoGrid}>
          {/* Row 1 */}
          <View style={styles.infoRow}>
            <View style={styles.infoCell}>
              <Text style={styles.infoLabel}>Código Operador:</Text>
              <Text style={styles.infoValue}>{aviso.codigoOperador || "-"}</Text>
            </View>
            <View style={[styles.infoCell, styles.infoCellRight]}>
              <Text style={styles.infoLabel}>Aviso de Cobro:</Text>
              <Text style={styles.infoValue}>{aviso.numeroAviso || "-"}</Text>
            </View>
          </View>

          {/* Row 2 */}
          <View style={styles.infoRow}>
            <View style={styles.infoCell}>
              <Text style={styles.infoLabel}>Nombre Operador:</Text>
              <Text style={styles.infoValue}>{aviso.nombreOperador || "-"}</Text>
            </View>
            <View style={[styles.infoCell, styles.infoCellRight]}>
              <Text style={styles.infoLabel}>Fecha Vencimiento:</Text>
              <Text style={styles.infoValue}>{fechaVencimiento}</Text>
            </View>
          </View>

          {/* Row 3 */}
          <View style={styles.infoRow}>
            <View style={styles.infoCell}>
              <Text style={styles.infoLabel}>Cuenta del Estado:</Text>
              <Text style={styles.infoValue}>
                CTA.12301 CONCESIONES Y FRECUENCIAS RADIOELECTRICAS
              </Text>
            </View>
            <View style={[styles.infoCell, styles.infoCellRight]}>
              <Text style={styles.infoLabel}>Fecha Emisión:</Text>
              <Text style={styles.infoValue}>{fechaEmision}</Text>
            </View>
          </View>

          {/* Row 4 */}
          <View style={styles.infoRow}>
            <View style={styles.infoCell}>
              <Text style={styles.infoLabel}>Servicio:</Text>
              <Text style={styles.infoValue}>{aviso.servicio || "-"}</Text>
            </View>
            <View style={[styles.infoCell, styles.infoCellRight]}>
              <Text style={styles.infoLabel}>Moneda:</Text>
              <Text style={styles.infoValue}>{aviso.moneda || "Lempiras"}</Text>
            </View>
          </View>

          {/* Row 5 - Total a Pagar */}
          <View style={styles.infoRow}>
            <View style={[styles.infoCell, styles.infoCellTotalLeft]}>
              {/* intentionally empty — acts as visual counterweight */}
            </View>
            <View style={[styles.infoCell, styles.infoCellRight, styles.infoCellTotalRight]}>
              <Text style={styles.infoLabel}>Total a Pagar:</Text>
              <Text style={[styles.infoValue, { fontFamily: "Helvetica-Bold", fontSize: 12 }]}>
                L. {formatCurrency(aviso.total)}
              </Text>
            </View>
          </View>
        </View>

        {/* Detalle Header */}
        <View style={styles.detalleHeader}>
          <Text style={styles.detalleHeaderText}>Detalle</Text>
        </View>

        {/* Detalle Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View style={styles.tableCellCC}>
              <Text style={styles.tableHeaderText}>CC</Text>
            </View>
            <View style={styles.tableCellConcepto}>
              <Text style={styles.tableHeaderText}>Concepto</Text>
            </View>
            <View style={styles.tableCellDescripcion}>
              <Text style={styles.tableHeaderText}>Descripción</Text>
            </View>
            <View style={styles.tableCellMonto}>
              <Text style={styles.tableHeaderText}>Monto</Text>
            </View>
          </View>

          {/* Table Rows */}
          {sortedDetalles.map((detalle, index) => (
            <View
              key={index}
              style={[styles.tableRow, index % 2 !== 0 && styles.tableRowAlt]}
            >
              <View style={styles.tableCellCC}>
                <Text style={styles.tableCellText}>
                  {detalle.codigoConceptoCobro || "-"}
                </Text>
              </View>
              <View style={styles.tableCellConcepto}>
                <Text style={styles.tableCellText}>
                  {detalle.concepto || "-"}
                </Text>
              </View>
              <View style={styles.tableCellDescripcion}>
                <Text style={styles.tableCellText}>
                  {detalle.descripcion || "-"}
                </Text>
              </View>
              <View style={styles.tableCellMonto}>
                <Text style={[styles.tableCellText, { textAlign: "right" }]}>
                  L. {formatCurrency(detalle.valor)}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* Separator Line */}
        <View style={styles.separator} />

        {/* FIN DE LÍNEA Text */}
        <Text style={styles.finLineaText}>FIN DE LÍNEA</Text>

        {/* Monto en Letras */}
        <View style={styles.montoLetrasContainer}>
          <Text style={styles.montoLetrasText}>{totalEnLetras}</Text>
        </View>

        {/* Footer - Sistema Usuario */}
        <View style={styles.footerContainer}>
          <Text style={styles.footerText}>
            Documento generado por:{" "}
            <Text style={styles.footerBold}>
              {aviso.SistemaUsuario || "Sistema SACC"}
            </Text>
          </Text>
        </View>

        {/* Watermark for test/prueba PDFs */}
        {isPrueba && (
          <View
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-45deg)",
              opacity: 0.1,
            }}
          >
            <Text
              style={{
                fontSize: 60,
                fontFamily: "Helvetica-Bold",
                color: "#ff0000",
              }}
            >
              PRUEBA
            </Text>
          </View>
        )}
      </Page>
    </Document>
  );
};

export default AvisoCobroPDF;
